<template>
	<p>{{ text.join('') }}</p>
</template>

<script setup lang="ts">
import { computed, toRefs } from 'vue';

import { MonetizationType } from '@/@types/graphql-types';

import type { TitleDetail } from '@/interfaces/title-details-graphql';
import { useSpinningText } from '@/helpers/composables/useSpinningText';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';

const { language } = storeToRefs(useLanguageStore());

const props = defineProps<{
	title: string;
	offers: TitleDetail['offers'];
	seasonNumber?: number | undefined;
}>();

const { getOfferTextBlock, getInfoTextBlock, getRTLInfoTextBlock } = useSpinningText(toRefs(props));

const text = computed(() => {
	switch (language.value) {
		case 'fi':
			return [
				getInfoTextBlock(
					MonetizationType.Flatrate,
					'Tällä hetkellä voit katsoa elokuvan',
					'suoratoistona palvelussa'
				),
				getOfferTextBlock(MonetizationType.Ads, ' tai ilmaiseksi mainosten kanssa palvelussa'),
				'. ',
				getInfoTextBlock(MonetizationType.Rent, 'Voit myös vuokrata elokuvan', 'netissä palvelussa'),
				getOfferTextBlock(MonetizationType.Buy, ' tai ladata sen palvelussa'),
				'. ',
			];
		case 'ru':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'В настоящее время Вы можете посмотреть', 'на'),
				getOfferTextBlock(MonetizationType.Ads, ' или бесплатно с рекламой на'),
				'. ',
				getInfoTextBlock(MonetizationType.Rent, 'Также возможно взять', 'напрокат на'),
				getOfferTextBlock(MonetizationType.Buy, ' онлайн и скачать на'),
				'. ',
			];
		case 'it':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Adesso puoi guardare', 'in streaming su'),
				getOfferTextBlock(MonetizationType.Ads, ' gratuitamente con avvisi pubblicitari su'),
				'. ',
				getInfoTextBlock(MonetizationType.Rent, 'Puoi anche noleggiare online', 'su'),
				getOfferTextBlock(MonetizationType.Buy, ' e scaricarlo da'),
				'. ',
			];
		case 'de':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Du kannst', 'im Abo bei'),
				getOfferTextBlock(
					MonetizationType.Ads,
					' oder bei',
					'kostenlos mit Werbeunterbrechungen im Stream anschauen'
				),
				'. ',
				getInfoTextBlock(MonetizationType.Rent, 'Zusätzlich ist', 'noch bei', 'online ausleihbar'),
				getOfferTextBlock(MonetizationType.Buy, ' und steht bei', 'als Download zur Verfügung'),
				'. ',
			];
		case 'fr':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'En ce moment, vous pouvez regarder', 'en streaming sur'),
				getOfferTextBlock(MonetizationType.Ads, ' ou gratuit avec publicités sur'),
				'. ',
				getInfoTextBlock(MonetizationType.Rent, 'Il est également possible de louer', 'sur', 'en ligne'),
				getOfferTextBlock(MonetizationType.Buy, ' ou de le télécharger sur'),
				'. ',
			];
		case 'es':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Actualmente, usted es capaz de ver', 'streaming en'),
				getOfferTextBlock(MonetizationType.Ads, ' o forma gratuita con anuncios en'),
				'. ',
				getInfoTextBlock(MonetizationType.Rent, 'Por lo tanto, es posible alquilar', 'en', 'online'),
				getOfferTextBlock(MonetizationType.Buy, ' y para comprarlo en'),
			];
		case 'ja':
			return [
				getInfoTextBlock(
					MonetizationType.Flatrate,
					`現在の「${props.title}」の視聴情報：`,
					'は',
					'で配信中です。'
				),
				getOfferTextBlock(MonetizationType.Ads, '広告付きの動画は', 'で無料配信中です。'),
				getOfferTextBlock(MonetizationType.Rent, 'また、', 'で配信版のレンタルができます。'),
				getOfferTextBlock(MonetizationType.Buy, '配信版を購入してダウンロードする場合は', 'です'),
				'。',
			];
		case 'ko':
			return [
				getOfferTextBlock(
					MonetizationType.Flatrate,
					'현재 ',
					`에서 "${props.title}" 스트리밍 서비스 중입니다. `
				),
				getOfferTextBlock(MonetizationType.Ads, '광고가 포함된 영상은 ', '에서 무료 배포 중입니다. '),
				getOfferTextBlock(MonetizationType.Rent, '또한 ', '에서 대여가 가능하며 '),
				getOfferTextBlock(MonetizationType.Buy, '', '에서 유료 다운로드하실 수 있습니다. '),
			];
		case 'ro':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'În prezent, puteți viziona', 'streaming pe'),
				getOfferTextBlock(MonetizationType.Ads, ' sau gratuit cu afișare de anunțuri pe'),
				'. ',
				getInfoTextBlock(MonetizationType.Rent, 'Puteți, de asemenea, să închiriați', 'pe', 'online'),
				getOfferTextBlock(MonetizationType.Buy, ' și să-l descărcați pe'),
				'. ',
			];
		case 'hu':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Jelenleg a(z)', 'adatfolyamként elérhető a(z)'),
				getOfferTextBlock(
					MonetizationType.Ads,
					' szolgáltatónál, vagy megtekinthető ingyenesen reklámokkal itt:'
				),
				'. ',
				getInfoTextBlock(
					MonetizationType.Rent,
					'Továbbá a(z)',
					'film online kibérelhető a(z)',
					'szolgáltatónál'
				),
				getOfferTextBlock(MonetizationType.Buy, ' illetve letölthető innen:'),
				'. ',
			];
		case 'cs':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Aktuálně máte možnost sledovat', 'streaming u'),
				getOfferTextBlock(MonetizationType.Ads, 'nebo zdarma s reklamami u'),
				'. ',
				getInfoTextBlock(MonetizationType.Rent, 'je také možné půjčit si online', 'u'),
				getOfferTextBlock(MonetizationType.Buy, ' a stáhnout jej u'),
				'. ',
			];
		case 'bg':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'В момента можете да гледате стрийминг на', 'в', 'онлайн'),
				getOfferTextBlock(MonetizationType.Ads, ' или безплатно с реклами в'),
				'. ',
				getInfoTextBlock(MonetizationType.Rent, 'Възможно е също да наемете', 'в', 'онлайн'),
				getOfferTextBlock(MonetizationType.Buy, ' онлайн и да го изтеглите в'),
				'. ',
			];
		case 'ar':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'يمكنكم حالياً مشاهدة', 'على'),
				getOfferTextBlock(MonetizationType.Ads, ' أو مجاناً مع فواصل إعلانية على'),
				'. ',
				getInfoTextBlock(MonetizationType.Rent, 'بإمكانكم أيضاً تأجير', 'على', 'أونلاين'),
				getOfferTextBlock(MonetizationType.Buy, ' أو تنزيله على'),
				'. ',
			];
		case 'pt':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Atualmente você pode assistir', 'em streaming via'),
				getOfferTextBlock(MonetizationType.Ads, ' ou gratuitamente com anúncios via'),
				'. ',
				getInfoTextBlock(MonetizationType.Rent, 'Também é possível aluga', 'online via'),
				getOfferTextBlock(MonetizationType.Buy, ' e para download via'),
				'. ',
			];
		case 'tr':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Şu an', 'adlı yapımı', 'üzerinden veya'),
				getOfferTextBlock(MonetizationType.Ads, ' platformunda ücretsiz ama reklamlı olarak izleyebilirsiniz'),
				'. ',
				getInfoTextBlock(
					MonetizationType.Rent,
					'',
					'adlı yapımı ',
					'platformlarından çevrimiçi olarak kiralamak ve'
				),
				getOfferTextBlock(MonetizationType.Buy, ' ', 'üzerinden indirmek de mümkün'),
				'. ',
			];
		case 'sq':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Aktualisht, ju mund të shikoni', 'në'),
				getOfferTextBlock(MonetizationType.Ads, ' apo falas me reklama në'),
				'. ',
				getInfoTextBlock(MonetizationType.Rent, 'Gjithashtu është e mundur të merrni me qira', 'në', 'online'),
				getOfferTextBlock(MonetizationType.Buy, ' dhe ta shkarkoni atë në'),
				'. ',
			];
		case 'pl':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Obecnie możesz oglądać', 'w streamingu w serwisie'),
				getOfferTextBlock(MonetizationType.Ads, ' lub za darmo z reklamami w serwisie'),
				'. ',
				getInfoTextBlock(MonetizationType.Rent, 'Można również wypożyczyć', 'online w serwisie'),
				getOfferTextBlock(MonetizationType.Buy, ' oraz pobrać jako plik w serwisie'),
				'. ',
			];
		case 'az':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Hazırda siz', 'üzərindən'),
				getOfferTextBlock(
					MonetizationType.Ads,
					' yayımını',
					'üzərindən reklamlarla ödənişsiz izləyə bilərsiniz'
				),
				'. ',
				getInfoTextBlock(MonetizationType.Rent, 'Həmçinin', 'filmini'),
				getOfferTextBlock(
					MonetizationType.Buy,
					' saytında onlayn icarəyə götürə və onu',
					'saytından endirə bilərsiniz'
				),
				'. ',
			];
		case 'el':
			return [
				getInfoTextBlock(
					MonetizationType.Flatrate,
					'Προς το παρόν μπορείτε να παρακολουθήσετε τον τίτλο',
					'με streaming στο'
				),
				getOfferTextBlock(MonetizationType.Ads, ' ή δωρεάν με διαφημίσεις στο'),
				'. ',
				getInfoTextBlock(MonetizationType.Rent, 'Υπάρχει επίσης η δυνατότητα να ενοικιάσετε τον τίτλο', 'στο'),
				getOfferTextBlock(MonetizationType.Buy, ' online και να τον κατεβάσετε στο'),
				'. ',
			];
		case 'sr':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Trenutno možete da gledate', 'strim na'),
				getOfferTextBlock(MonetizationType.Ads, ' ili besplatno sa oglasima na'),
				'. ',
				getInfoTextBlock(MonetizationType.Rent, 'Takođe je moguće iznajmiti', 'na'),
				getOfferTextBlock(MonetizationType.Buy, ' onlajn i preuzeti ga na'),
				'. ',
			];
		case 'sv':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'För närvarande kan du se', 'streama på'),
				getOfferTextBlock(MonetizationType.Ads, ' eller gratis med annonser på'),
				'. ',
				getInfoTextBlock(MonetizationType.Rent, 'Det är också möjligt att hyra', 'på'),
				getOfferTextBlock(MonetizationType.Buy, ' online och ladda ner den på'),
				'. ',
			];
		case 'uk':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Наразі ви можете дивитися трансляцію', 'на'),
				getOfferTextBlock(MonetizationType.Ads, ' aбо дивитися її безкоштовно з рекламою на'),
				'. ',
				getInfoTextBlock(MonetizationType.Rent, 'Ви також можете взяти', 'напрокат онлайн на'),
				getOfferTextBlock(MonetizationType.Buy, ' і завантажити її на'),
				'. ',
			];
		case 'ur':
			return [
				getRTLInfoTextBlock(MonetizationType.Flatrate, 'فی الحال آپ ', 'پر'),
				getOfferTextBlock(
					MonetizationType.Ads,
					' اسٹریمنگ دیکھنے کے قابل ہیں یا',
					'پر اشتہارات کے ساتھ مفت دیکھ سکتے ہیں۔'
				),
				' ',
				getRTLInfoTextBlock(MonetizationType.Rent, 'آن لائن', 'پر'),
				getOfferTextBlock(MonetizationType.Buy, ' کرائے پر لینا اور اسے ', 'پر ڈاؤن لوڈ کرنا بھی ممکن ہے۔'),
			];
		case 'zh':
			return [
				getRTLInfoTextBlock(MonetizationType.Flatrate, '目前您可以在', '上串流觀看', ','),
				getOfferTextBlock(MonetizationType.Ads, ' 或透過', '免費收看'),
				'。',
				getRTLInfoTextBlock(MonetizationType.Rent, '您也可以在', '線上租借 ', ','),
				getOfferTextBlock(MonetizationType.Buy, ' 並在', '上下載觀看'),
				'。',
			];
		case 'en':
		default:
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Currently you are able to watch', 'streaming on'),
				getOfferTextBlock(MonetizationType.Ads, ' or for free with ads on'),
				'. ',
				getInfoTextBlock(MonetizationType.Rent, 'It is also possible to rent', 'on', 'online'),
				getOfferTextBlock(MonetizationType.Buy, ' and to download it on'),
				'. ',
			];
	}
});
</script>
